import { Component, OnInit, Input, OnChanges, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { BaseComponent } from '../../../base/base-component.component';
import { TranslateService } from '@ngx-translate/core';
import { Schema, Content, PersistenceStorageService, FilterPagination, DynamicContentSchemaFactory, AuthenticationService } from '../../../services';
import { FormGroup, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, EMPTY, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AppToken } from '../../../models/app-token';

/**
 * If this component has to become more complex (example: you need a lazy loading process for the listings)
 * Advice: make a smart component of this, then create 2 render component for each listing
 * It will make you an easier futur
 */

@Component({
  selector: 'app-contents-listing',
  templateUrl: './contents-listing.component.html',
  styleUrls: ['./contents-listing.component.css']
})
export class ContentsListingComponent extends BaseComponent implements OnInit, OnChanges {

  @Input()
  schemaDefinition: Schema;

  @Input()
  selectedContent: string;

  @Input()
  selectableContents: Content[];

  @Input()
  baseUrl: string;

  @Input()
  filter: string;

  @Input()
  contentCount: number;

  @Input()
  currentPageNumber: number;

  @Input()
  paginateSize: number;

  @Input()
  maxSize: number;

  @Input()
  orderBySwitch: boolean;

  @Output()
  searchFilter: EventEmitter<FilterPagination> = new EventEmitter();

  alphaOrder = false;
  allContents = false;
  reverseOrder = false;

  @ViewChild("searchbar", null)
  searchBar: ElementRef;

  alphabet = [
    '0', '1', '2',
    '3', '4', '5',
    '6', '7', '8',
    '9',
    'a', 'b', 'c',
    'd', 'e', 'f',
    'g', 'h', 'i',
    'j', 'k', 'l',
    'm', 'n', 'o',
    'p', 'q', 'r',
    's', 't', 'u',
    'v', 'w', 'x',
    'y', 'z'
  ];

  paginateListContents: Content[] = [];

  contentDictionary = {};

  searchFormGroup: FormGroup;
  isSearching = false;

  searchResult: Content[] = [];

  alphaOrderKey: string;

  filterValue: string = '';

  userIsOwner: boolean;
  userIsDev: boolean;
  userIsEditor: boolean;
  userIsReadWrite: boolean;
  userIsReader: boolean;


  constructor(
    private readonly route: ActivatedRoute,
    readonly router: Router,
    translateService: TranslateService,
    private readonly contentFacadeFactory: DynamicContentSchemaFactory,
    private readonly persistenceStorageService: PersistenceStorageService,
    private readonly authenticationService: AuthenticationService
  ) {
    super(translateService);
    this.setTranslateContext('ContentsListingComponent');
  }

  ngOnInit() {

    this.userIsOwner = this.authenticationService.userHasRole("Owner");
    this.userIsDev = this.authenticationService.userHasRole("Developer");
    this.userIsEditor = this.authenticationService.userHasRole("Editor");
    this.userIsReadWrite = this.authenticationService.userHasRole("ReadWrite");
    this.userIsReader = this.authenticationService.userHasRole("Reader");

    if (!this.paginateSize) {
      this.paginateSize = AppToken.CONTENTS_ITEMS_PER_PAGE;
    }

    this.initSearchFormGroup();

    this.alphaOrderKey = JSON.stringify(this.baseUrl + 'alphaOrder');

    const storedOrder = this.persistenceStorageService.get(this.alphaOrderKey);

    this.updateStoredOrderList();

    if (storedOrder) {
      this.alphaOrder = true;
    }
    if (this.alphaOrder) {
      this.listToggle(this.alphaOrder);
    }
  }

  updateStoredOrderList() {
    this.contentDictionary = {};
    this.selectableContents.forEach(content => {
      const firstLetter = content.data.code.iv[0].toLowerCase();
      if (!this.contentDictionary[firstLetter]) {
        this.contentDictionary[firstLetter] = [];
      }
      this.contentDictionary[firstLetter].push(content);
    });

    //sort alphabetically each letter
    for (const letter in this.contentDictionary) {
      this.contentDictionary[letter].sort((a, b) => {
        if (a.data.code.iv.toUpperCase() < b.data.code.iv.toUpperCase()) {
          return -1;
        }
        if (a.data.code.iv.toUpperCase() > b.data.code.iv.toUpperCase()) {
          return 1;
        }
        return 0;
      });
    }

  }

  getContents(contentFromPage: number, contentCount: number | null) {
    this.currentPageNumber = contentFromPage;
    this.paginateSize = contentCount;
    this.launchResearch();
  }

  ngAfterViewInit() {
    if (this.filter) {
      this.searchBar.nativeElement.value = this.filter;
      this.filterValue = this.filter;
    }
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  ngOnChanges() {
    this.selectedContent = this.route.snapshot.paramMap.get('code');
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  //getContentDefinition(contentName: string): Observable<DynamicContentModel> {
  //  return this.dynamicContentFacto.getDynamicContentFacade(contentName).getContentDefinition();
  //}

  isOpenLetter(letter: string): boolean {
    if (this.selectedContent && this.selectedContent.length > 0 && this.selectedContent.charAt(0).toLowerCase() === letter) {
      return true;
    }
    return false;
  }

  initSearchFormGroup() {
    const formControl = new FormControl();
    const subscription = formControl
      .valueChanges
      .pipe(debounceTime(AppToken.INPUT_DELAY_TIMESPAN))
      .pipe(distinctUntilChanged())
      .subscribe(result => {
        const t = result as string;
        this.launchResearch(t.toLowerCase());
      });
    this.searchFormGroup = new FormGroup({ search: formControl });
    //test
    this.subscriptions.push(subscription);
  }

  launchResearch(filterValue?: string): void {

    if (filterValue) {
      this.currentPageNumber = 1;
    }

    const filter = new FilterPagination();

    if (this.paginateSize) {
      filter.from = (this.currentPageNumber - 1) * this.paginateSize;
      filter.to = filter.from + this.paginateSize;
    } else {
      filter.from = 0;
      filter.to = null;
    }

    const ordering = this.reverseOrder ? 'descending' : 'ascending';
    filter.orderBy = `data.index.iv ascending,data.code.iv ${ordering}`
    filter.filterObject = this.getSearchObject();

    this.filterValue = filterValue;

    this.searchFilter.emit(filter);
  }

  getSearchObject(): object {
    const filter = (this.searchBar !== undefined && this.searchBar.nativeElement !== undefined && this.searchBar.nativeElement.value !== null) ? this.searchBar.nativeElement.value : EMPTY;
    const oSearch = {
      or:
        [
          {
            path: 'data.code.iv',
            op: 'contains',
            value: filter
          }
        ]

    }
    return oSearch;
  }

  redirect(contentName: string) {
    if (!this.searchBar.nativeElement.value && this.currentPageNumber <= 1) {
      this.router.navigateByUrl(`${this.baseUrl}/${contentName}`);
    } else {
      this.router.navigate([`${this.baseUrl}/${contentName}`, { filter: this.searchBar.nativeElement.value, page: this.currentPageNumber }]);
    }
  }

  redirectBase() {
    if (this.baseUrl === '/app-resource') {
      this.router.navigateByUrl('resource');
    } else {
      this.router.navigateByUrl(`${this.baseUrl}`);
    }
  }

  listToggle(isAlpha: boolean) {
    this.alphaOrder = isAlpha;

    this.persistenceStorageService.set(this.alphaOrderKey, this.alphaOrder);

    if (this.alphaOrder) {
      this.getContents(1, AppToken.CONTENTS_ITEMS_PER_REQUEST);
      this.updateStoredOrderList();
    } else {
      this.getContents(1, AppToken.CONTENTS_ITEMS_PER_PAGE);
    }

  }

  toggleAll(allContents: boolean) {
    this.allContents = allContents;

    this.reverseOrder = !this.reverseOrder

    if (this.allContents) {
      this.getContents(1, AppToken.CONTENTS_ITEMS_PER_REQUEST);
    } else {
      this.getContents(1, AppToken.CONTENTS_ITEMS_PER_PAGE);
    }
  }

  updatePaginateList($event: any): void {
    this.currentPageNumber = $event.page;
    this.paginateSize = $event.itemsPerPage;
    this.launchResearch();
  }

  //When you drop an elelemnt of the list, come here
  pageContentDrop(event) {
    if (this.filterValue === "" && !this.alphaOrder) {
      if (this.orderBySwitch == null) {
        console.warn("orderBySwitch is not set, using normal ordering by default");
        this.normalReorder(event);
      } else if (!this.orderBySwitch) {
        this.normalReorder(event);
      } else {
        this.reorderBySwitch(event);
      }
    }
  }

  normalReorder(event) {
    const temp: Content[] = this.clone(this.selectableContents);
    const contentFacade = this.contentFacadeFactory.getDynamicContentSchemaFacade(this.schemaDefinition.name);
    const maxIndex = event['previousIndex'] > event['currentIndex'] ? event['previousIndex'] : event['currentIndex'];
    const minIndex = event['previousIndex'] < event['currentIndex'] ? event['previousIndex'] : event['currentIndex'];
    const obsList: Observable<Content>[] = [];

    //update content pos in list
    this.selectableContents = this.moveArray(this.selectableContents, event['previousIndex'], event['currentIndex']);

    //Get content tu update
    for (let i = minIndex; i < maxIndex; i++) {
      const contentId = temp[i].id;
      obsList.push(contentFacade.get(contentId, true));
    }

    //update contents in squidex
    this.subscriptions = this.subscriptions.concat(forkJoin(obsList).subscribe(() => {
      for (let i = minIndex; i <= maxIndex; i++) {
        const pc = this.selectableContents[i];
        const pcData = this.clone(pc.data);
        this.dParser.putData(pcData, 'index', i);
        contentFacade.update(pc.id, pcData).subscribe();
      }
    }));
  }

  cancelResearch() {
    this.isSearching = false;
    this.searchBar.nativeElement.value = "";
    this.listToggle(false);
  }

  //https://stackoverflow.com/questions/5306680/move-an-array-element-from-one-array-position-to-another
  // Maybe create a tool script for arrays ?
  moveArray(array: any[], from: number, to: number): any[] {
    array.splice(to, 0, array.splice(from, 1)[0]);
    return array;
  }

  //[a,b,c,d] if you move a => c become [c,b,a,d]
  //Can be call defining [orderBySwitch] = "false"
  reorderBySwitch(event) {
    const contentsOrdered: Content[] = this.clone(this.selectableContents);
    const temp = contentsOrdered[event['previousIndex']];
    contentsOrdered[event['previousIndex']] = contentsOrdered[event['currentIndex']];
    contentsOrdered[event['currentIndex']] = temp;
    this.selectableContents = contentsOrdered;
    const contentFacade = this.contentFacadeFactory.getDynamicContentSchemaFacade(this.schemaDefinition.name);

    // TODO: this has to be improve, there is no point to update every content here, just update the 2 that just changed
    for (let i = 0; i < contentsOrdered.length; i++) {
      const contentId = contentsOrdered[i].id;
      const sub = contentFacade.get(contentId, true).subscribe(pc => {
        const pcData = this.clone(pc.data);
        this.dParser.putData(pcData, 'index', i);
        contentFacade.update(contentId, pcData).subscribe();
        this.subscriptions.push(sub);
      });
    }
  }
}
